// src/Dashboard/pages/user/Boutique/components/CartDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaCartPlus,
  FaMinus,
  FaPlus,
  FaRegCreditCard,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import { toast } from "react-toastify";

import { createReglement } from "../../../../../services/achatsServices";
import { getArticles, updateArticle } from "../../../../../services/articlesServices";
import {
  sendNotification,
  sendNotificationToAdmin,
} from "../../../../../services/notificationServices";

import {
  FALLBACK_ARTICLE_IMAGE,
  formatEUR,
  getCartTotal,
  getCartUnits,
  getArticleImage,
  normalizeArticle,
  toNumberSafe,
} from "../utils/boutiqueUtils";

export default function CartDrawer({
  isOpen,
  cart,
  setCart,
  user,
  onClose,
  onRemove,
  onOrderSuccess,
  onGoReglements,
}) {
  const [ordering, setOrdering] = useState(false);

  const totalPrice = useMemo(() => getCartTotal(cart), [cart]);
  const totalUnits = useMemo(() => getCartUnits(cart), [cart]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleQuantityChange = (id, increment) => {
    setCart((previous) =>
      previous.map((item) => {
        if (item.id !== id) return item;

        const nextQuantity = Math.max(1, toNumberSafe(item.quantity) + increment);

        if (
          item.is_stockable &&
          nextQuantity > toNumberSafe(item.quantite_stock)
        ) {
          toast.warn(`Stock insuffisant pour "${item.nom}".`);
          return item;
        }

        return {
          ...item,
          quantity: nextQuantity,
        };
      })
    );
  };

  const handleMessageChange = (index, value) => {
    setCart((previous) =>
      previous.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              message: value,
            }
          : item
      )
    );
  };

  const handleOrder = async () => {
    if (!cart.length) {
      toast.warn("Votre panier est vide.");
      return;
    }

    const local = JSON.parse(localStorage.getItem("userInfo") || "{}");

    const userAchatId = local?.userId || user?.id || user?.userId;

    const token =
      local?.token ||
      user?.token ||
      user?.accessToken ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("token") ||
      null;

    if (!userAchatId) {
      toast.error("Session utilisateur invalide. Veuillez vous reconnecter.");
      return;
    }

    try {
      setOrdering(true);

      const freshArticlesData = await getArticles();
      const freshArticles = Array.isArray(freshArticlesData)
        ? freshArticlesData.map(normalizeArticle)
        : [];

      const dateAchat = new Date().toISOString();
      const orderDetails = [];

      for (const item of cart) {
        const fresh = freshArticles.find((article) => article.id === item.id);
        const source = fresh || item;

        if (
          source.is_stockable &&
          toNumberSafe(source.quantite_stock) < toNumberSafe(item.quantity)
        ) {
          toast.error(`Stock insuffisant pour "${item.nom}".`);
          setOrdering(false);
          return;
        }

        const quantity = Math.max(1, toNumberSafe(item.quantity));
        const unitPrice = toNumberSafe(item.prix);
        const total = unitPrice * quantity;

        await createReglement({
          user_achat_id: userAchatId,
          article_id: item.id,
          quantite: quantity,
          prix_total: total,
          date_achat: dateAchat,
          message_achat: item.message || "",
          statut_reglement: "Non Réglé",
          type_reglement: "En ligne",
        });

        orderDetails.push(`${quantity}× ${item.nom} (${formatEUR(unitPrice)})`);

        if (source.is_stockable) {
          const newStock = Math.max(
            0,
            toNumberSafe(source.quantite_stock) - quantity
          );

          await updateArticle(item.id, {
            quantite_stock: newStock,
          });
        }
      }

      if (token) {
        try {
          await sendNotification(
            `Commande enregistrée : ${orderDetails.join(", ")}.`,
            userAchatId,
            token
          );
        } catch (error) {
          console.error("Notification utilisateur échouée :", error);
        }

        try {
          const adminName = `${local?.prenom || user?.prenom || ""} ${
            local?.nom || user?.nom || ""
          }`.trim();

          await sendNotificationToAdmin(
            `${adminName || "Un utilisateur"} a commandé : ${orderDetails.join(
              ", "
            )}.`,
            token
          );
        } catch (error) {
          console.error("Notification admin échouée :", error);
        }
      }

      toast.success("Commande passée avec succès.");

      setCart([]);
      await onOrderSuccess?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de la commande :", error);
      toast.error("Erreur lors de la commande.");
    } finally {
      setOrdering(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-5xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              Panier boutique
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white sm:text-xl">
              Votre panier
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={ordering}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 disabled:opacity-60 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <FaTimes />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {!cart.length ? (
            <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 px-5 py-12 text-center dark:border-slate-700 dark:bg-slate-800/40">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-slate-400 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
                <FaCartPlus className="text-2xl" />
              </div>

              <h3 className="mt-5 text-lg font-bold text-slate-900 dark:text-white">
                Votre panier est vide
              </h3>

              <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
                Ajoutez un ou plusieurs articles depuis la boutique avant de
                passer commande.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map((item, index) => {
                const image = getArticleImage(item);
                const lineTotal = toNumberSafe(item.prix) * toNumberSafe(item.quantity);

                return (
                  <article
                    key={item.id}
                    className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950"
                  >
                    <div className="grid gap-4 p-4 sm:grid-cols-[150px_minmax(0,1fr)]">
                      <div className="overflow-hidden rounded-2xl bg-slate-100 dark:bg-slate-900">
                        <img
                          src={image}
                          alt={item.nom}
                          className="h-40 w-full object-cover sm:h-full"
                          loading="lazy"
                          onError={(event) => {
                            event.currentTarget.src = FALLBACK_ARTICLE_IMAGE;
                          }}
                        />
                      </div>

                      <div className="min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="line-clamp-2 text-base font-bold text-slate-900 dark:text-white">
                              {item.nom}
                            </h3>

                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                              {formatEUR(item.prix)} / unité
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => onRemove(item.id)}
                            disabled={ordering}
                            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-700 ring-1 ring-red-200 transition hover:bg-red-100 disabled:opacity-60 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20"
                          >
                            <FaTrash />
                          </button>
                        </div>

                        {item.description && (
                          <p className="mt-3 line-clamp-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                            {item.description}
                          </p>
                        )}

                        <div className="mt-3 flex flex-wrap gap-2 text-xs">
                          {item.taille && (
                            <span className="rounded-full bg-slate-100 px-3 py-1 font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700">
                              Taille : {item.taille}
                            </span>
                          )}

                          {item.is_stockable && (
                            <span className="rounded-full bg-emerald-50 px-3 py-1 font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
                              Stock : {item.quantite_stock}
                            </span>
                          )}
                        </div>

                        <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                          <div className="inline-flex w-fit items-center rounded-2xl border border-slate-200 bg-slate-50 p-1 dark:border-slate-800 dark:bg-slate-900">
                            <button
                              type="button"
                              onClick={() => handleQuantityChange(item.id, -1)}
                              disabled={ordering || item.quantity <= 1}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-700 transition hover:bg-white disabled:opacity-40 dark:text-slate-200 dark:hover:bg-slate-800"
                            >
                              <FaMinus />
                            </button>

                            <span className="min-w-12 px-3 text-center text-sm font-bold text-slate-900 dark:text-white">
                              {item.quantity}
                            </span>

                            <button
                              type="button"
                              onClick={() => handleQuantityChange(item.id, 1)}
                              disabled={ordering}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-700 transition hover:bg-white disabled:opacity-40 dark:text-slate-200 dark:hover:bg-slate-800"
                            >
                              <FaPlus />
                            </button>
                          </div>

                          <p className="text-xl font-bold text-slate-900 dark:text-white">
                            {formatEUR(lineTotal)}
                          </p>
                        </div>

                        <input
                          type="text"
                          value={item.message || ""}
                          onChange={(event) =>
                            handleMessageChange(index, event.target.value)
                          }
                          disabled={ordering}
                          placeholder="Message pour le club : taille précise, prénom, remarque..."
                          className="mt-4 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        />
                      </div>
                    </div>
                  </article>
                );
              })}

              <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-bold text-slate-900 dark:text-white">
                      Total panier
                    </p>

                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      {totalUnits} article{totalUnits > 1 ? "s" : ""} à commander
                    </p>
                  </div>

                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {formatEUR(totalPrice)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <footer className="border-t border-slate-200 bg-white/95 p-4 dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={ordering}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Continuer mes achats
            </button>

            <button
              type="button"
              onClick={handleOrder}
              disabled={ordering || !cart.length}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaCartPlus />
              {ordering ? "Commande..." : "Passer la commande"}
            </button>

            <button
              type="button"
              onClick={onGoReglements}
              disabled={ordering}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
            >
              <FaRegCreditCard />
              Commandes & Règlements
            </button>
          </div>
        </footer>
      </aside>
    </div>
  );
}